import { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AgentBalanceLedgerApi, AgentBalancesApi, UsersApi, TenantUsersApi } from "../../../api/common/apiIndex";
import countries from "i18n-iso-countries";
import "i18n-iso-countries/langs/en.json";
import "./AgentDetails.css";
import Loader from "../../common/commonUtils/Loader";
import UserProfileCard from "../team/UserProfileCard";
import EditEntityModal from "../../common/commonUtils/EditEntityModal";
import RoleManagerModal from "../team/RoleManagerModal";
import showConfirmToast from "../businessUtilities/ConfirmToast";
import { useAuth } from "../../../context/AuthProvider";
import MoreActionsDropdown from "../../common/commonUtils/MoreActionsDropdown";
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));

const fmtMoney = (n, currency = "USD") =>
  new Intl.NumberFormat("sv-SE", { style: "currency", currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n || 0));

const ALL_TYPES = ["FLOAT_IN", "REFUND", "RELEASE", "PAYOUT", "FLOAT_BACK", "RESERVATION"];
const inflowTypes = new Set(["FLOAT_IN", "REFUND", "RELEASE"]);
const outflowTypes = new Set(["PAYOUT", "FLOAT_BACK", "RESERVATION"]);

const typeBadgeClass = (t) => {
  if (inflowTypes.has(t)) return "bg-success-subtle text-success-emphasis border border-success-subtle";
  if (outflowTypes.has(t)) return "bg-danger-subtle text-danger-emphasis border border-danger-subtle";
  return "bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle";
};

export default function AgentDetails({ agentUuid: agentUuidOverride } = {}) {
  const { agentUuid: routeAgentUuid } = useParams();
  const agentUuid = agentUuidOverride ?? routeAgentUuid;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const currentUser = auth?.user ?? null;
  const hasRole = auth?.hasRole ?? (() => false);
  const isOwner = hasRole("BUSINESS_OWNER");
  const isAdmin = hasRole("ADMIN");

  const [page, setPage] = useState(0);
  const [size] = useState(15);
  const [sort, setSort] = useState("createdAt,desc");
  const [typeFilter, setTypeFilter] = useState(new Set(ALL_TYPES));
  const [q, setQ] = useState("");
  const [hasTxOnly, setHasTxOnly] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  useEffect(() => { setPage(0); }, [sort]);

  const agentQuery = useQuery({
    queryKey: ["agent", agentUuid],
    queryFn: () => UsersApi.getByUuid(agentUuid),
    enabled: typeof agentUuid === "string" && agentUuid.length > 0,
    staleTime: 300_000,
    retry: 1,
  });

  const invalidateAgentData = () => {
    if (agentUuid) {
      queryClient.invalidateQueries({ queryKey: ["agent", agentUuid] });
    }
    queryClient.invalidateQueries({ queryKey: ["agents"] });
    queryClient.invalidateQueries({
      predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === "agents-all",
    });
    queryClient.invalidateQueries({ queryKey: ["tenant-users"] });
    queryClient.invalidateQueries({ queryKey: ['agent-balance-snapshots'] });
    agentQuery.refetch();
  };

  const agent = agentQuery.data || null;
  const agentId = agent?.id ?? null;
  const roles = useMemo(() => new Set(agent?.roles || []), [agent?.roles]);
  const targetIsOwner = roles.has("BUSINESS_OWNER");
  const targetIsAdmin = roles.has("ADMIN");
  const targetIsAgent = roles.has("AGENT");
  const isSelf = currentUser?.id === agent?.id;
  const canEditProfile = Boolean(agent) && (isOwner || (isAdmin && !targetIsOwner));
  const canManageRoles = Boolean(agent) && isOwner;
  const canToggleStatus =
    Boolean(agent) && (isOwner || (isAdmin && targetIsAgent && !targetIsAdmin && !targetIsOwner));
  const canDelete =
    Boolean(agent) && (isOwner || (isAdmin && targetIsAgent && !targetIsAdmin && !targetIsOwner));

  useEffect(() => { setPage(0); }, [agentId]);

  const hasAgentId = agentId !== null && agentId !== undefined && String(agentId).length > 0;

  const ledgerQuery = useQuery({
    queryKey: ["agent-ledger", String(agentId || ""), page, size, sort],
    queryFn: () => AgentBalanceLedgerApi.listByAgentId(agentId, { page, size, sort }),
    enabled: hasAgentId,
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const pageData = ledgerQuery.data || {};
  const rows = useMemo(() =>{
    return pageData.content || []
  },[pageData.content]);
  const totalPages = pageData.totalPages || 0;
  const totalElements = pageData.totalElements || 0;

  const firstRow = rows?.[0] ?? null;

  const currency3 = useMemo(() => {
    const c = firstRow?.currency || agent?.currency || agent?.defaultCurrency || "";
    return (c || "").toUpperCase();
  }, [firstRow, agent?.currency, agent?.defaultCurrency]);

  const country2 = useMemo(() => {
    const raw = firstRow?.country || agent?.countryCode || agent?.country || "";
    if (!raw) return "";
    if (raw.length === 2) return raw.toUpperCase();
    const iso2 = countries.getAlpha2Code(raw, "en");
    return (iso2 || "").toUpperCase();
  }, [firstRow, agent?.countryCode, agent?.country]);

  const hasCountry = country2.length === 2;
  const hasCurrency = currency3.length === 3;
  const snapshotReady = hasAgentId && hasCountry && hasCurrency;

  const snapshotQuery = useQuery({
    queryKey: ["agent-snapshot", Number(agentId || 0), country2, currency3],
    queryFn: () => AgentBalancesApi.getSnapshot({ agentId, country: country2, currency: currency3 }),
    enabled: snapshotReady,
    keepPreviousData: true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const rowsByDate = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const key = String(r.createdAt).slice(0, 10);
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(r);
    }
    const desc = sort.includes("createdAt,desc");
    const keys = Array.from(m.keys()).sort((a, b) => (desc ? (a < b ? 1 : -1) : (a > b ? 1 : -1)));
    return keys.map((k) => ({ date: k, items: m.get(k) }));
  }, [rows, sort]);

  const filteredRowsByDate = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return rowsByDate
      .map(({ date, items }) => ({
        date,
        items: items.filter((r) => {
          if (!typeFilter.has(r.type)) return false;
          if (hasTxOnly && !r.txUuid) return false;
          if (!qLower) return true;
          const hay = `${r.reason || ""} ${r.txUuid || ""} ${r.createdBy || ""} ${r.actorName || ""}`.toLowerCase();
          return hay.includes(qLower);
        }),
      }))
      .filter((d) => d.items.length > 0);
  }, [rowsByDate, typeFilter, q, hasTxOnly]);

  const rangeText = useMemo(() => {
    if (!totalElements) return "0–0 of 0";
    const start = page * size + 1;
    const end = Math.min(start + rows.length - 1, totalElements);
    return `${start}–${end} of ${totalElements}`;
  }, [page, size, rows.length, totalElements]);


  const agentCreatedAt = agent?.createdAt ? new Date(agent.createdAt).toLocaleString() : "—";
  const agentLastLogin = agent?.lastLogin ? new Date(agent.lastLogin).toLocaleString() : "Never";

  const setPreset = (kind) => {
    if (kind === "ALL") setTypeFilter(new Set(ALL_TYPES));
    else if (kind === "INFLOW") setTypeFilter(new Set(Array.from(inflowTypes)));
    else if (kind === "OUTFLOW") setTypeFilter(new Set(Array.from(outflowTypes)));
    else if (kind === "RECLASS") setTypeFilter(new Set(["RESERVATION", "RELEASE"]));
  };
  const snapshot = snapshotQuery.data || null;

  const coverage = useMemo(() => {
    if (!snapshotReady || !snapshot) return null;
    const available = Number(snapshot.available || 0);
    const reserved = Number(snapshot.reserved || 0);
    const total = available + reserved;               
    const ratio = reserved > 0 ? available / reserved : Infinity; 
    return { available, reserved, total, ratio };
  }, [snapshotReady, snapshot]);

  const exportCsv = () => {
    const headers = ["id", "createdAt", "type", "amount", "currency", "country", "reason", "txUuid", "createdBy"];
    const lines = [headers.join(",")].concat(
      rows.map((r) =>
        [
          r.id,
          new Date(r.createdAt).toISOString(),
          r.type,
          Number(r.amount || 0),
          r.currency || "",
          r.country || "",
          (r.reason || "").replace(/[\n\r,]+/g, " "),
          r.txUuid || "",
          r.actorName || "",
        ].join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agent_${agentId}_ledger_page_${page + 1}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleError = (err, fallback) => {
    const status = err?.response?.status;
    if (status === 403) {
      toast.error(err?.response?.data?.message || "You don't have permission to perform this action.");
      return;
    }
    toast.error(err?.response?.data?.message || err?.message || fallback);
  };

  const updateMutation = useMutation({
    mutationFn: (dto) => {
      if (!agent?.id) throw new Error("Missing agent ID.");
      return TenantUsersApi.update(agent.id, dto);
    },
    onSuccess: () => {
      toast.success("Agent updated.");
      setShowEditModal(false);
      invalidateAgentData();
    },
    onError: (err) => handleError(err, "Failed to update agent."),
  });

  const statusMutation = useMutation({
    mutationFn: (active) => {
      if (!agent?.id) throw new Error("Missing agent ID.");
      return TenantUsersApi.updateStatus(agent.id, active);
    },
    onSuccess: () => {
      toast.success("Status updated.");
      invalidateAgentData();
    },
    onError: (err) => handleError(err, "Failed to update status."),
  });

  const roleMutation = useMutation({
    mutationFn: (rolesList) => {
      if (!agent?.id) throw new Error("Missing agent ID.");
      return TenantUsersApi.updateRoles(agent.id, rolesList);
    },
    onSuccess: () => {
      toast.success("Roles updated.");
      setShowRoleModal(false);
      invalidateAgentData();
    },
    onError: (err) => handleError(err, "Failed to update roles."),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!agent?.id) throw new Error("Missing agent ID.");
      return TenantUsersApi.remove(agent.id);
    },
    onSuccess: () => {
      toast.success("Agent deleted.");
      queryClient.invalidateQueries({ queryKey: ["agents"] });
      queryClient.invalidateQueries({ queryKey: ["tenant-users"] });
      queryClient.invalidateQueries({ queryKey: ['agent-balance-snapshots'] });
      navigate("/home/agents");
    },
    onError: (err) => handleError(err, "Failed to delete agent."),
  });

  const handleToggleStatus = () => {
    if (!agent) return;
    statusMutation.mutate(!agent.active);
  };

  const handleDelete = () => {
    if (!agent) return;
    showConfirmToast({
      message: `Delete ${agent.firstName || agent.email}?`,
      onConfirm: () => deleteMutation.mutate(),
      id: `delete-agent-${agent.id}`,
    });
  };

  if (agentQuery.isLoading || (agentQuery.isFetching && !agent)) {
    return <div className="d-flex justify-content-center align-items-center py-5"><Loader text="Loading…" /></div>;
  }

  if (agentQuery.isError) {
    const msg = agentQuery.error?.response?.data?.message || agentQuery.error?.message || "Failed to load agent.";
    return <div className="alert alert-danger my-3">{msg}</div>;
  }

  return (
    <div className="container-fluid px-0">
      <div className="hero hero-pad py-4">
        <div className="container py-4">
          <div className="mb-4">
            <UserProfileCard
              user={agent}
              createdAt={agentCreatedAt}
              lastLogin={agentLastLogin}
              actions={<>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  {canEditProfile && (
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill"
                      onClick={() => setShowEditModal(true)}
                      disabled={updateMutation.isPending}
                    >
                      Edit profile
                    </button>
                  )}
                  <MoreActionsDropdown
                    label="More"
                    items={(() => {
                      const list = [];
                      if (canManageRoles) {
                        list.push({
                          key: "roles",
                          label: "Change roles",
                          onClick: () => setShowRoleModal(true),
                          disabled: roleMutation.isPending,
                        });
                      }
                      list.push({
                        key: "export",
                        label: "Export CSV",
                        onClick: exportCsv,
                      });
                      if ((canToggleStatus || canDelete) && list.length > 0) {
                        list.push({ type: "divider" });
                      }
                      if (canToggleStatus) {
                        list.push({
                          key: "status",
                          label: agent.active ? "Deactivate" : "Activate",
                          onClick: handleToggleStatus,
                          disabled: statusMutation.isPending,
                        });
                      }
                      if (canDelete) {
                        list.push({
                          key: "delete",
                          label: "Delete",
                          onClick: handleDelete,
                          danger: true,
                          disabled: deleteMutation.isPending,
                        });
                      }
                      return list;
                    })()}
                  />
                  {!canEditProfile && !canManageRoles && !canToggleStatus && !canDelete && (
                    <div className="text-body-secondary small">
                      You have view-only access to this agent.
                    </div>
                  )}
                </div>
              </>}
            />
          </div>

          <div className="card border rounded-4 shadow-sm">
            <div className="card-body p-3 p-sm-4">
              <div className="row g-3">
                <div className="col-12 col-md-4">
                  <div className="card border rounded-4 shadow-sm h-100">
                    <div className="card-body">
                      <div className="small text-body-secondary">Available</div>
                      <div className="fs-4 fw-semibold">
                        {!snapshotReady
                          ? "—"
                          : snapshotQuery.isLoading && !snapshotQuery.data
                            ? "…"
                            : fmtMoney(snapshotQuery.data?.available ?? 0, currency3 || "USD")}
                      </div>
                      <div className="text-body-secondary small mt-1">Currency: {hasCurrency ? currency3 : "—"}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="card border rounded-4 shadow-sm h-100">
                    <div className="card-body">
                      <div className="small text-body-secondary">Reserved (current)</div>
                      <div className="fs-4 fw-semibold">
                        {!snapshotReady
                          ? "—"
                          : snapshotQuery.isLoading && !snapshotQuery.data
                            ? "…"
                            : fmtMoney(snapshotQuery.data?.reserved ?? 0, currency3)}
                      </div>
                      <div className="text-body-secondary small mt-1">Held for pending payouts</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="card border rounded-4 shadow-sm h-100">
                    <div className="card-body">
                      <div className="d-flex justify-content-between small">
                        <span>Available</span>
                        <strong>{snapshotReady ? fmtMoney(coverage?.available ?? 0, currency3) : "—"}</strong>
                      </div>
                      <div className="d-flex justify-content-between small">
                        <span>Reserved</span>
                        <strong>{snapshotReady ? fmtMoney(coverage?.reserved ?? 0, currency3) : "—"}</strong>
                      </div>

                      <hr className="my-2" />

                      <div className="d-flex justify-content-between small">
                        <span>Total</span>
                        <strong>{snapshotReady ? fmtMoney(coverage?.total ?? 0, currency3) : "—"}</strong>
                      </div>

                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container pt-0 sticky-top py-2">
        <div className="card border rounded-4 shadow-sm mb-0">
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <div className="btn-group btn-group-sm me-auto" role="group" aria-label="Quick presets">
              <button className="btn btn-outline-secondary" onClick={() => setPreset("ALL")}>All</button>
              <button className="btn btn-outline-success" onClick={() => setPreset("INFLOW")}>Inflow</button>
              <button className="btn btn-outline-danger" onClick={() => setPreset("OUTFLOW")}>Outflow</button>
              <button className="btn btn-outline-secondary" onClick={() => setPreset("RECLASS")}>Reclass</button>
            </div>

            <div className="form-check form-switch ms-sm-auto filter-item">
              <input
                className="form-check-input"
                type="checkbox"
                id="hasTxOnly"
                checked={hasTxOnly}
                onChange={(e) => setHasTxOnly(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="hasTxOnly">
                Only entries with Tx
              </label>
            </div>

            <input
              className="form-control form-control-sm w-auto filter-item"
              placeholder="Search reason / tx / by"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ minWidth: 220, maxWidth: 320 }}
              aria-label="Search ledger"
            />

            <select
              className="form-select form-select-sm w-auto filter-item"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Sort ledger"
            >
              <option value="createdAt,desc">Newest</option>
              <option value="createdAt,asc">Oldest</option>
              <option value="amount,desc">Amount ↓</option>
              <option value="amount,asc">Amount ↑</option>
            </select>
          </div>
        </div>
      </div>

      <div
        className="container my-3"
        style={{ paddingBottom: "calc(var(--app-safe-area-bottom) + 2.5rem)" }}
      >
        <div className="card border rounded-4 shadow-sm">
          <div className="card-header bg-body rounded-top-4 p-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <h6 className="mb-0">Ledger Activity</h6>
              <span className="ms-auto small text-body-secondary">{rangeText}</span>
            </div>
          </div>

          <div className="card-body">
            {ledgerQuery.isError && (
              <div className="alert alert-danger m-0">
                {ledgerQuery.error?.response?.data?.message || ledgerQuery.error?.message || "Failed to load ledger."}
              </div>
            )}

            {ledgerQuery.isLoading && (
              <div className="d-flex justify-content-center align-items-center py-4">
                <Loader text="Loading…" />
              </div>
            )}

            {!ledgerQuery.isLoading && filteredRowsByDate.length === 0 && (
              <div className="text-center text-body-secondary py-5">No entries found.</div>
            )}

            {filteredRowsByDate.length > 0 && (
              <div className="d-flex flex-column gap-4">
                {filteredRowsByDate.map(({ date, items }) => {
                  const toCents = (x) => Math.round(Number(x || 0) * 100);
                  const netCents = items.reduce((acc, r) => acc + toCents(r.amount), 0);
                  const dayNet = netCents / 100;
                  const dayCurrency = items[0]?.currency || currency3 || "USD";

                  return (
                    <section key={date} className="d-flex flex-column flex-sm-row gap-2 gap-sm-3">
                      <div className="date-col">
                        <div className="fw-semibold">{new Date(`${date}T00:00:00`).toLocaleDateString()}</div>
                        <div className={`small ${dayNet >= 0 ? "text-success" : "text-danger"}`}>
                          {fmtMoney(dayNet, dayCurrency)}
                        </div>
                      </div>

                      <div className="flex-grow-1 timeline">
                        <div className="timeline-rail" />
                        <div className="d-flex flex-column gap-2">
                          {items.map((r) => {
                            const isIn = inflowTypes.has(r.type);
                            return (
                              <div key={r.id} className="timeline-item">
                                <span className={`timeline-dot ${isIn ? "bg-success" : "bg-danger"}`} />
                                <div className="flex-grow-1 p-2 p-sm-3 border rounded-3 hover-shadow-sm">
                                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                      <span className={`badge ${typeBadgeClass(r.type)} rounded-pill`}>{r.type}</span>
                                      <span className="small text-body-secondary">{new Date(r.createdAt).toLocaleTimeString()}</span>
                                    </div>
                                    <div className={`fw-semibold ${isIn ? "text-success" : "text-danger"}`}>
                                      {fmtMoney(r.amount, r.currency || currency3 || "USD")}
                                    </div>
                                  </div>
                                  <div className="d-flex flex-wrap gap-3 small mt-1">
                                    <span className="text-body-secondary">By: {r.actorName || "-"}</span>
                                    {r.txUuid && (r.type === 'RESERVATION' || r.type === 'PAYOUT') &&(
                                      <button
                                        className="btn btn-link btn-sm p-0"
                                        onClick={() => navigate(`/home/transaction/${r.txUuid}`)}
                                        aria-label={`Open transaction ${r.txUuid}`}
                                      >
                                        TX {r.txUuid.slice(0, 8)}…
                                      </button>
                                    )}
                                  </div>
                                  {r.reason ? <div className="mt-1 small">{r.reason}</div> : null}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>

          <div className="card-footer bg-body rounded-bottom-4 d-flex justify-content-between align-items-center">
            <small className="text-body-secondary">{rangeText}</small>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary btn-sm rounded-pill"
                disabled={page === 0 || ledgerQuery.isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </button>
              <span className="small text-body-secondary">
                Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
                {ledgerQuery.isFetching ? " …" : ""}
              </span>
              <button
                className="btn btn-outline-secondary btn-sm rounded-pill"
                disabled={page + 1 >= totalPages || ledgerQuery.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      {showEditModal && (
        <EditEntityModal
          title="Edit agent"
          row={agent}
          isBusy={updateMutation.isPending}
          onClose={() => setShowEditModal(false)}
          onSubmit={(dto) => updateMutation.mutate(dto)}
        />
      )}

      {showRoleModal && (
        <RoleManagerModal
          roles={agent?.roles}
          submitting={roleMutation.isPending}
          onClose={() => setShowRoleModal(false)}
          onSave={(updatedRoles) => roleMutation.mutate(updatedRoles)}
          disableOwnerRemoval={isSelf}
        />
      )}
    </div>
  );
}
